import { IconHeart, IconUser } from '@tabler/icons-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import DeliveryDropdown from 'composition/navigation/DeliveryDropdown'
import ProfileDropdown from 'composition/navigation/ProfileDropdown'
import { useNavigation } from 'composition/navigation/useNavigation'
import { isAlgoliaIntegrationConfigured } from 'lib/algolia'
import { CartDrawer, useDisplayCart } from 'modules/cart'
import { CategoriesNavigationMobile } from 'modules/catalog'
import { useRibbon } from 'modules/content'
import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import { GuestDeliveryAreaCheck, useDeliverySupport } from 'modules/delivery'
import {
  FetchUpsellingResponse,
  NavMenuCategory,
  ProductTypes
} from 'modules/products'
import { cartAdapters } from 'modules/saleOrders'
import { Search } from 'modules/search'
import LinkButton from 'shared/ui/Button/LinkButton'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import styles from './NavigationMobile.module.scss'

interface NavigationMobileProps {
  promotedProducts: ProductTypes[]
  categories: NavMenuCategory[]
  menuPromotionItems: FetchUpsellingResponse['data'] | null
}

const NavigationMobile = ({
  categories,
  menuPromotionItems
}: NavigationMobileProps) => {
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const isLoggedIn = isNotNilOrEmpty(user)
  const cart = useDisplayCart()
  const router = useRouter()
  const { isPostalCodeAndCitySupported } = useDeliverySupport()

  const { openablesState, handleOpenablesStateToggle } = useNavigation()
  const { ribbon } = useRibbon()

  const handleRedirect = (path: string) => () => {
    router.push(path)
  }

  return (
    <>
      <nav className={styles.wrapper}>
        <div className={styles.content}>
          <Link
            className={styles.logoLink}
            href={PATHS.home}
            {...addTestId('navigation-logo')}
          >
            <Image
              priority
              src='/static/lokalny-rolnik.svg'
              alt='Logo Lokalny Rolnik'
              width={139}
              height={43}
            />
          </Link>
          {isLoggedIn ? (
            <div className={styles.icons}>
              <DeliveryDropdown openPostalCode={openablesState.postalCode} />
              <ProfileDropdown isMobile />
              <button
                className={styles.menuIcon}
                onClick={handleRedirect(PATHS.profileFavourites)}
              >
                <IconHeart />
                <div className={styles.iconLabel}>Ulubione</div>
              </button>
              {isNotNilOrEmpty(cart) && (
                <CartDrawer
                  ports={cartAdapters}
                  open={openablesState.cart}
                  isRibbonActive={!!ribbon}
                  setOpen={handleOpenablesStateToggle}
                />
              )}
            </div>
          ) : (
            <div className={styles.navbarRightWrapper}>
              <GuestDeliveryAreaCheck
                savePostalCode
                open={openablesState.deliveryReservationNoUser}
                setOpen={handleOpenablesStateToggle}
                {...addTestId('delivery-no-user')}
              />

              <div className={styles.navLinks}>
                <LinkButton
                  href={PATHS.login}
                  size='large'
                  variant='neutralOutline'
                  aria-label='Logowanie'
                  {...addTestId('login-button')}
                >
                  <IconUser width={24} height={24} />
                </LinkButton>
                <div className={styles.onlyTabletContent}>
                  <Link
                    className={styles.navLink}
                    href={PATHS.register}
                    {...addTestId('register-button')}
                  >
                    Rejestracja
                  </Link>
                </div>
              </div>
              {isNotNilOrEmpty(cart) && isPostalCodeAndCitySupported && (
                <CartDrawer
                  ports={cartAdapters}
                  open={openablesState.cart}
                  isRibbonActive={!!ribbon}
                  setOpen={handleOpenablesStateToggle}
                />
              )}
            </div>
          )}
        </div>
        <div className={styles.bottomBar}>
          <CategoriesNavigationMobile
            categories={categories}
            menuPromotionItems={menuPromotionItems}
          />
          {isAlgoliaIntegrationConfigured && (
            <Search
              open={openablesState.searchDropdown}
              setOpen={handleOpenablesStateToggle}
              isRibbonActive={!!ribbon}
              {...addTestId('search-dropdown')}
            />
          )}
        </div>
      </nav>
    </>
  )
}

export default NavigationMobile
